/**
 * Thin backend adapter for the new frontend.
 *
 * Keeping all request details here prevents endpoint knowledge from leaking
 * into the view or playback layers.
 */
export class ApiClient {
  constructor() {
    this.hypnosisProtocol = null;
  }

  setHypnosisProtocol(protocol) {
    this.hypnosisProtocol = protocol && typeof protocol === "object" ? protocol : null;
  }

  async fetchJson(path, options = {}) {
    const response = await fetch(path, {
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
      ...options,
    });

    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`${path} failed (${response.status}): ${detail}`);
    }

    return response.json();
  }

  fetchSession() {
    return this.fetchJson("/api/session");
  }

  fetchSessionBuilderOptions() {
    return this.fetchJson("/api/session-builder/options");
  }

  fetchSessionBuilderPreferences() {
    return this.fetchJson("/api/session-builder/preferences");
  }

  saveSessionBuilderPreferences(preferences) {
    return this.fetchJson("/api/session-builder/preferences", {
      method: "POST",
      body: JSON.stringify(preferences || {}),
    });
  }

  compileSession(request) {
    return this.fetchJson("/api/session-builder/compile", {
      method: "POST",
      body: JSON.stringify(request || {}),
    });
  }

  /** Refresh the current custom session without resetting its text-job state. */
  refreshSession(request) {
    return this.fetchJson("/api/session-builder/compile", {
      method: "POST",
      body: JSON.stringify({ ...(request || {}), _refresh_existing: true }),
    });
  }

  fetchThemes() {
    return this.fetchJson("/api/themes").then((payload) => payload.themes || []);
  }

  fetchTags() {
    return this.fetchJson("/api/tags");
  }

  fetchBackground() {
    return this.fetchJson("/api/background").then((payload) => payload.files || []);
  }

  fetchCovert() {
    return this.fetchJson("/api/covert").then((payload) => payload.tracks || []);
  }

  fetchPersonas(theme = "") {
    const suffix = theme ? `?theme=${encodeURIComponent(theme)}` : "";
    return this.fetchJson(`/api/personas${suffix}`).then((payload) => payload.personas || []);
  }

  fetchPersonaSample(personaId) {
    return this.fetchJson(`/api/persona/sample?persona_id=${encodeURIComponent(personaId)}`);
  }

  fetchLibrary(channel, { lite = false } = {}) {
    const options = arguments[1] || {};
    const exclude = options.exclude || "";
    const arc_stage = options.arc_stage || "";
    const sort = options.sort || "newest";
    const params = new URLSearchParams({ ch: channel });
    if (lite) params.set("lite", "1");
    if (exclude) params.set("exclude", exclude);
    if (arc_stage) params.set("arc_stage", arc_stage);
    if (sort) params.set("sort", sort);
    return this.fetchJson(`/api/library?${params.toString()}`);
  }

  fetchOverlaySchedule(file, channel, exclude = "") {
    const params = new URLSearchParams({ file, ch: channel });
    // Recently played overlay files — the server penalizes these during
    // schedule resolution so lanes rotate instead of repeating one clip.
    if (exclude) params.set("exclude", exclude);
    return this.fetchJson(`/api/overlay-schedule?${params.toString()}`);
  }

  updatePreferences(preferences) {
    return this.fetchJson("/api/preferences", {
      method: "POST",
      body: JSON.stringify(preferences || {}),
    });
  }

  setTheme(theme) {
    return this.fetchJson("/api/theme", {
      method: "POST",
      body: JSON.stringify({ theme }),
    });
  }

  replaceTags(tags) {
    return this.fetchJson("/api/tags/set", {
      method: "POST",
      body: JSON.stringify({ tags }),
    });
  }

  toggleTag(tag) {
    return this.fetchJson(`/api/tags/toggle/${encodeURIComponent(tag)}`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  sendFeedback(payload) {
    return this.fetchJson("/api/feedback", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  favoriteTrack(filename) {
    return this.fetchJson("/api/favorite", {
      method: "POST",
      body: JSON.stringify({ filename }),
    });
  }

  followPersona(personaId) {
    return this.fetchJson("/api/fan/follow", {
      method: "POST",
      body: JSON.stringify({ persona_id: personaId }),
    });
  }

  selectPersona(personaId) {
    return this.fetchJson("/api/persona/select", {
      method: "POST",
      body: JSON.stringify({ persona_id: personaId }),
    });
  }

  goldStarPersona({ personaId, filename = "", category = "", theme = "" }) {
    return this.fetchJson("/api/persona/gold_star", {
      method: "POST",
      body: JSON.stringify({
        persona_id: personaId,
        filename,
        category,
        theme,
        honor: "sent_them_deep",
      }),
    });
  }

  tipPersona({ personaId, amount, message = "" }) {
    return this.fetchJson("/api/persona/tip", {
      method: "POST",
      body: JSON.stringify({
        persona_id: personaId,
        amount,
        message,
      }),
    });
  }

  logPlayback(event) {
    // Fire-and-forget — never let logging errors surface to the player
    fetch("/api/playback/log", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...event,
        hypnosis_protocol_id: this.hypnosisProtocol?.protocol_id || "",
        hypnosis_protocol_block: this.hypnosisProtocol?.block_index,
      }),
    }).catch(() => { });
  }

  recordHypnosisOutcome({ metric, value, channel = "", context = {} }) {
    return this.fetchJson("/api/hypnosis/outcome", {
      method: "POST",
      body: JSON.stringify({
        // The server assigns the current protocol when the client has not
        // cached one yet (notably for freshly compiled/custom sessions).
        protocol_id: this.hypnosisProtocol?.protocol_id || "",
        metric,
        value,
        channel,
        context,
      }),
    });
  }

  fetchSpectralCondition(trialIndex = 0) {
    const params = new URLSearchParams({ trial: String(Math.max(0, Math.trunc(Number(trialIndex) || 0))) });
    return this.fetchJson(`/api/hypnosis/spectral-condition?${params.toString()}`);
  }

  recordSpectralObservation({
    trialIndex = 0,
    detectionResponse,
    transportProfile = {},
    signalEvent = {},
    channel = "",
  } = {}) {
    return this.fetchJson("/api/hypnosis/spectral-observation", {
      method: "POST",
      body: JSON.stringify({
        trial_index: Math.max(0, Math.trunc(Number(trialIndex) || 0)),
        detection_response: detectionResponse,
        transport_profile: transportProfile,
        signal_event: signalEvent,
        channel,
      }),
    });
  }

  acceptTos(version) {
    return this.fetchJson("/api/tos/accept", {
      method: "POST",
      body: JSON.stringify({ accepted: true, version }),
    });
  }

  fetchWallet() {
    return this.fetchJson("/api/wallet");
  }

  createPaypalOrder(packId) {
    return this.fetchJson("/api/wallet/paypal/create-order", {
      method: "POST",
      body: JSON.stringify({ pack_id: packId }),
    });
  }

  capturePaypalOrder(orderId) {
    return this.fetchJson("/api/wallet/paypal/capture-order", {
      method: "POST",
      body: JSON.stringify({ order_id: orderId }),
    });
  }
}
